import { useState, useEffect, useRef, useCallback } from 'react';
import { Link, Outlet, useLocation, useNavigate } from 'react-router-dom';
import { useAuthStore, api } from '../lib/auth';
import { useTranslation, useI18nStore } from '../lib/i18n';
import { useSystemSettings } from '../hooks/useSystemSettings';
import { useTheme, COLOR_THEMES } from '../hooks/useTheme';
import { useRealtimeMessages } from '../hooks/useRealtimeMessages';
import { Button } from '../components/ui/button';
import { 
  DropdownMenu, 
  DropdownMenuContent, 
  DropdownMenuItem, 
  DropdownMenuSeparator,
  DropdownMenuTrigger 
} from '../components/ui/dropdown-menu';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '../components/ui/tooltip';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '../components/ui/dialog';
import {
  Search,
  Calendar, 
  LayoutDashboard, 
  Users, 
  Building2, 
  Settings, Settings2, 
  FileText, 
  ClipboardList,
  LogOut, 
  Menu, 
  X,
  User,
  ChevronDown,
  ChevronRight,
  Globe,
  Bell,
  ListChecks,
  CalendarDays,
  ServerCog,
  CalendarOff,
  BarChart3,
  Grid3X3,
  Package,
  PanelLeftClose,
  PanelLeftOpen,
  FolderOpen,
  Truck,
  Car,
  PieChart,
  Fuel,
  Languages,
  Upload,
  ExternalLink,
  Moon,
  Sun,
  Database,
  Send,
  Eye,
  EyeOff,
  Lock,
  Palette,
  Check,
  ShieldAlert,
  Mail,
  Inbox,
  MessageSquare,
  Sparkles,
  List,
  ClipboardCheck,
  Calendar as CalendarIcon,
  Cloud,
  Euro,
  Flag,
  Activity,
  Briefcase,
  Clock,
  HardDrive,
  Archive,
  ArrowLeftRight,
  FolderTree,
  TrendingUp,
  Receipt,
  Flame,
  GitCompare,
  UserCheck,
  FileX,
  PlayCircle,
  Map as MapIcon,
} from 'lucide-react';
import PostNLIcon from '../components/icons/PostNLIcon';

import PushNotificationPrompt from '../components/PushNotificationPrompt';
import StickyNotesWidget from '../components/StickyNotesWidget';
import PWAStatus from '../components/PWAStatus';
import MessengerChatHeads from '../components/MessengerChatHeads';
import { useLockStore } from '../store/useLockStore';
import { usePrivacyStore } from '../store/usePrivacyStore';
import { useFeatureFlag } from '../lib/useFeatureFlag';
import { useFavorites, flattenMenu, FAV_ROLES } from '../lib/favorites';
import { FavoriteStar } from '../components/favorites/FavoriteStar';
import { FavoritesWheel } from '../components/favorites/FavoritesWheel';

// Boş yetki listesi = optIn olmayan her şey açık; doluysa sadece listedekiler.
const menuAllowed = (it, allowed) => {
  if (it.pageKey === 'dashboard') return true;
  if (!allowed.length) return !it.optIn;
  if (!it.pageKey) return true;
  return (Array.isArray(it.pageKey) ? it.pageKey : [it.pageKey]).some((k) => allowed.includes(k));
};
const filterMenuByPages = (items, allowed) => items
  .filter((it) => menuAllowed(it, allowed))
  .map((it) => (it.children ? { ...it, children: it.children.filter((c) => menuAllowed(c, allowed)) } : it))
  .filter((it) => !it.children || it.children.length > 0);


export const DashboardLayout = () => {
  const { t } = useTranslation();
  const { language, setLanguage } = useI18nStore();
  const { user, logout, fetchUser, updateLanguage } = useAuthStore();
  const { settings } = useSystemSettings();
  const { theme, toggleTheme, colorTheme, setColorTheme } = useTheme();
  // Şub 16, 2026 — Feature flag'ler menü item'larını gate'ler
  const canEmployeeFinance = useFeatureFlag('employee_self_finance');
  const location = useLocation();
  const navigate = useNavigate();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(() => {
    // Load collapsed state from localStorage
    const saved = localStorage.getItem('sidebarCollapsed');
    return saved === 'true';
  });
  // Track if we're on desktop (lg breakpoint)
  const [isDesktop, setIsDesktop] = useState(() => typeof window !== 'undefined' && window.innerWidth >= 1024);
  useEffect(() => {
    const handler = () => setIsDesktop(window.innerWidth >= 1024);
    window.addEventListener('resize', handler);
    return () => window.removeEventListener('resize', handler);
  }, []);
  // Single-open accordion for sidebar groups — clicking a new group auto-
  // closes the previously-open one so the sidebar never becomes a scroll
  // nightmare with 4 groups open at once. `null` = everything collapsed.
  // On mount we seed it with the group matching the current route so the
  // user lands on a page with that group already expanded.
  // Persist the last-opened group across reloads so the user's mental
  // model of the sidebar survives F5. We read-on-mount and write every
  // time `openGroupKey` changes. `null` means "everything collapsed" and
  // is stored as an explicit string so the initial-state branch can
  // distinguish "never set" from "deliberately closed".
  const [openGroupKey, setOpenGroupKey] = useState(() => {
    try {
      const saved = localStorage.getItem('sidebar.openGroupKey');
      return saved && saved !== '__null__' ? saved : null;
    } catch { return null; }
  });
  useEffect(() => {
    try {
      localStorage.setItem('sidebar.openGroupKey', openGroupKey || '__null__');
    } catch { /* quota / private-mode safe */ }
  }, [openGroupKey]);

  // Şub 16, 2026 — Çalışan ilk kez girdiğinde "Kişisel" grubunu default'ta aç.
  // Ayrı bir bayrak (`sidebar.userSeededOnce`) ilk seed'i işaretler; kullanıcı
  // sonra manuel bir seçim yaparsa (grup açar veya tümünü kapatırsa) bayrak
  // zaten set olduğu için yeniden ezmez.
  const _sidebarSeededRef = useRef(false);
  useEffect(() => {
    if (!user || _sidebarSeededRef.current) return;
    _sidebarSeededRef.current = true;
    let alreadySeeded = null;
    try { alreadySeeded = localStorage.getItem('sidebar.userSeededOnce'); } catch { /* ignore LS errors */ }
    if (!alreadySeeded && user.role === 'EMPLOYEE') {
      setOpenGroupKey('personal');
      try { localStorage.setItem('sidebar.userSeededOnce', '1'); } catch { /* ignore LS errors */ }
    }
  }, [user]);
  // Keep the latest computed navItems in a ref so the route-sync effect
  // above can read them without adding the (recomputed-every-render) array
  // to its dependency list.
  const navItemsRef = useRef([]);
  // When the user navigates directly to a child URL (deep link, back
  // button, nav link inside a group), expand that group automatically
  // and collapse all others. `navItems` rebuilds on every render so we
  // don't gate this on a dependency array — the effect body is cheap.
  useEffect(() => {
    const items = navItemsRef.current || [];
    for (const it of items) {
      if (!it.isGroup || !Array.isArray(it.children)) continue;
      if (it.children.some((c) => c.path && (location.pathname === c.path || location.pathname.startsWith(c.path + '/')))) {
        setOpenGroupKey((cur) => (cur === it.groupKey ? cur : it.groupKey));
        return;
      }
    }
    // No group matches the active route — leave the state as-is so the
    // user can keep their manually-opened group visible while browsing
    // top-level pages (e.g. Dashboard → İletişim stays open).
  }, [location.pathname]);
  const [mobileSearchOpen, setMobileSearchOpen] = useState(false);
  const navRef = useRef(null);
  const [firmInfo, setFirmInfo] = useState(null);
  const [profileCheckLoading, setProfileCheckLoading] = useState(true);
  const [showProfileModal, setShowProfileModal] = useState(false);
  const [profileSettings, setProfileSettings] = useState({ require_profile_completion: true });
  const [profilePhotoUrl, setProfilePhotoUrl] = useState(null);
  const [hasPackages, setHasPackages] = useState(false);
  const [unreadNotifications, setUnreadNotifications] = useState(0);
  const [unreadMessages, setUnreadMessages] = useState(0);
  const [unreadChats, setUnreadChats] = useState(0);
  const [pendingForms, setPendingForms] = useState(0);  // Faz 2.5 — Bugünkü doldurulmamış form sayısı
  
  // Impersonate mode detection
  const isImpersonating = !!sessionStorage.getItem('admin-original-session');
  const originalAdminName = sessionStorage.getItem('admin-original-name') || 'Admin';
  
  const handleReturnToAdmin = () => {
    const originalSession = sessionStorage.getItem('admin-original-session');
    if (originalSession) {
      // Restore admin session
      localStorage.setItem('hr-auth-storage', originalSession);
      // Clean up impersonate data
      sessionStorage.removeItem('admin-original-session');
      sessionStorage.removeItem('admin-original-name');
      sessionStorage.removeItem('admin-original-role');
      // Full reload to restore admin state
      window.location.href = '/dashboard';
    }
  };
  
  const { lock: lockScreen } = useLockStore();
  const privacyMode = usePrivacyStore((s) => s.privacyMode);
  const togglePrivacyMode = usePrivacyStore((s) => s.togglePrivacyMode);
  const idleTimerRef = useRef(null);

  // User search
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [searchOpen, setSearchOpen] = useState(false);
  const searchRef = useRef(null);
  const searchTimeoutRef = useRef(null);
  
  // Close search on click outside
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (searchRef.current && !searchRef.current.contains(e.target)) {
        setSearchOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);
  
  const handleSearch = (query) => {
    const trimmed = query.trim();
    setSearchQuery(query);
    if (searchTimeoutRef.current) clearTimeout(searchTimeoutRef.current);
    if (!trimmed || trimmed.length < 2) {
      setSearchResults([]);
      setSearchOpen(false);
      return;
    }
    searchTimeoutRef.current = setTimeout(async () => {
      try {
        const [usersRes, vehiclesRes] = await Promise.all([
          api.get(`/admin/users?search=${encodeURIComponent(trimmed)}&limit=5`),
          api.get(`/vehicles?search=${encodeURIComponent(trimmed)}&limit=5`)
        ]);
        const users = (usersRes.data.success ? usersRes.data.data : []).map(u => ({ ...u, _type: 'user' }));
        const vehicles = (vehiclesRes.data.success ? (vehiclesRes.data.data?.items || vehiclesRes.data.data || []) : []).map(v => ({ ...v, _type: 'vehicle' }));
        setSearchResults([...users, ...vehicles]);
        setSearchOpen(true);
      } catch { setSearchResults([]); }
    }, 300);
  };
  
  const handleSearchSelect = (item, { newTab = true } = {}) => {
    setSearchOpen(false);
    setSearchQuery('');
    const url = item._type === 'vehicle'
      ? `/dashboard/vehicles/${item.id}`
      : `/dashboard/employees/${item.id}`;
    if (newTab) {
      // Yeni sekmede aç — operatör mevcut ekranını kaybetmiyor (planning /
      // mail vs'de kalıyor), arama sonucu yan sekmede açılıyor.
      window.open(url, '_blank', 'noopener,noreferrer');
    } else {
      navigate(url);
    }
  };

  // Fetch unread notification count
  useEffect(() => {
    const fetchUnreadCount = async () => {
      if (!user) return;
      try {
        const response = await api.get('/push/my-notifications/unread-count');
        if (response.data.success) {
          setUnreadNotifications(response.data.count || 0);
        }
      } catch (error) {
        console.error('Error fetching unread notifications:', error);
      }
      try {
        const msgRes = await api.get('/messages/unread-count');
        if (msgRes.data?.success) {
          setUnreadMessages(msgRes.data.data?.count || 0);
        }
      } catch {}
      try {
        const chatRes = await api.get('/chat/unread-count');
        if (chatRes.data?.success) {
          setUnreadChats(chatRes.data.data?.count || 0);
        }
      } catch {}
      // Faz 2.5: Bugünkü doldurulmamış form sayısı
      try {
        const formRes = await api.get('/planning/my-todays-assignment');
        const items = formRes.data?.data || [];
        const pending = items.filter((a) =>
          a.is_today && (a.form_template || []).length > 0 && !a.form_submitted
        ).length;
        setPendingForms(pending);
      } catch { setPendingForms(0); }
    };
    fetchUnreadCount();
    
    // Refresh every 60 seconds (reduced since WebSocket handles real-time)
    const interval = setInterval(fetchUnreadCount, 60000);
    return () => clearInterval(interval);
  }, [user]);

  // New message popup state
  const [msgPopup, setMsgPopup] = useState(null);
  const popupTimerRef = useRef(null);

  // Real-time WebSocket: new messages increase badge, expose onlineUsers globally
  const handleNewMessage = useCallback((msg) => {
    setUnreadMessages(prev => prev + 1);
    // Dispatch custom event so Messages page can react
    window.dispatchEvent(new CustomEvent('ws:new_message', { detail: msg }));
    // Show popup notification
    setMsgPopup(msg);
    if (popupTimerRef.current) clearTimeout(popupTimerRef.current);
    popupTimerRef.current = setTimeout(() => setMsgPopup(null), 8000);
  }, []);

  const handleReadReceipt = useCallback((data) => {
    window.dispatchEvent(new CustomEvent('ws:read_receipt', { detail: data }));
  }, []);

  const handleTyping = useCallback((data) => {
    window.dispatchEvent(new CustomEvent('ws:typing', { detail: data }));
  }, []);

  // Chat message popup replaced by MessengerChatHeads (Messenger-style mini
  // windows). See components/MessengerChatHeads.jsx.

  const handleChatMessage = useCallback((msg) => {
    // Increment badge if not on that chat conversation
    if (!location.pathname.startsWith('/dashboard/chat')) {
      setUnreadChats(prev => prev + 1);
    }
    // Fan-out to MessengerChatHeads (mini chat windows) and any other
    // listeners (e.g., the Chat page). The old auto-dismissing popup is
    // now replaced by a persistent Messenger-style chat head.
    window.dispatchEvent(new CustomEvent('ws:chat_message', { detail: msg }));
  }, [location.pathname]);

  const handleChatTyping = useCallback((data) => {
    window.dispatchEvent(new CustomEvent('ws:chat_typing', { detail: data }));
  }, []);

  const handleChatRead = useCallback((data) => {
    window.dispatchEvent(new CustomEvent('ws:chat_read', { detail: data }));
  }, []);

  const { onlineUsers, connected: wsConnected, sendWsMessage } = useRealtimeMessages({
    onNewMessage: handleNewMessage,
    onReadReceipt: handleReadReceipt,
    onTyping: handleTyping,
    onChatMessage: handleChatMessage,
    onChatTyping: handleChatTyping,
    onChatRead: handleChatRead,
  });

  // Track whether we've ever received a real online_status payload from server.
  // Before the first server-sent update, `onlineUsers` is still the default [] and
  // dispatching it would wipe out any REST-fetched state in consuming pages (Chat.jsx).
  const hasRealOnlineDataRef = useRef(false);
  useEffect(() => {
    if (wsConnected && onlineUsers && onlineUsers.length > 0) {
      hasRealOnlineDataRef.current = true;
    }
  }, [wsConnected, onlineUsers]);

  // Expose onlineUsers on window so Messages.jsx / Chat.jsx can access without prop drilling.
  useEffect(() => {
    window.__onlineUsers = onlineUsers;
    window.__wsConnected = wsConnected;
    window.__sendWsMessage = sendWsMessage;
    // Only dispatch once we actually have real data — otherwise initial [] would
    // race with and overwrite the REST-fetched online list on consumer pages.
    if (hasRealOnlineDataRef.current) {
      window.dispatchEvent(new CustomEvent('ws:online_status', { detail: onlineUsers }));
    }
  }, [onlineUsers, wsConnected, sendWsMessage]);

  // Check if user has packages (for EMPLOYEE role)
  useEffect(() => {
    const checkUserPackages = async () => {
      if (!user || user.role !== 'EMPLOYEE') {
        return;
      }
      try {
        const response = await api.get(`/loonbrief/user-packages/${user.id}`);
        if (response.data.success && response.data.data) {
          const { total_quantity } = response.data.data;
          setHasPackages(total_quantity > 0);
        }
      } catch (error) {
        console.error('Error checking user packages:', error);
        setHasPackages(false);
      }
    };
    checkUserPackages();
  }, [user]);

  // Save collapsed state to localStorage
  useEffect(() => {
    localStorage.setItem('sidebarCollapsed', sidebarCollapsed.toString());
  }, [sidebarCollapsed]);

  // Auto-collapse sidebar on data-dense pages so the board / chat thread gets
  // the full viewport. Only the "Planlama Yap" board wants it — Listele and
  // Özet read fine at the normal width.
  useEffect(() => {
    const p = location.pathname;
    if (
      p.startsWith('/dashboard/messages') ||
      p.startsWith('/dashboard/chat') ||
      p === '/dashboard/planning' ||
      p === '/dashboard/planning/board'
    ) {
      setSidebarCollapsed(true);
    }
  }, [location.pathname]);

  useEffect(() => {
    fetchUser();
  }, [fetchUser]);

  // Check if profile completion is required
  useEffect(() => {
    const checkProfileCompletion = async () => {
      if (!user) {
        setProfileCheckLoading(false);
        return;
      }
      
      // Skip for admins
      if (user.role === 'SUPER_ADMIN') {
        setProfileCheckLoading(false);
        return;
      }

      // Mart 01, 2026 — SUBCO için profil tamamlama zorunluluğu yok (sadeleştirme)
      if (user.role === 'SUBCO') {
        setProfileCheckLoading(false);
        return;
      }

      try {
        // Check profile settings
        const settingsResponse = await api.get('/employee-profile/me');
        if (settingsResponse.data.success) {
          const data = settingsResponse.data.data;
          // Show modal if profile not completed and setting requires it
          if (!data.profile_completed && profileSettings.require_profile_completion) {
            // Don't show on profile page itself
            if (!location.pathname.includes('/my-profile')) {
              setShowProfileModal(true);
            }
          }
        }
      } catch (error) {
        console.error('Failed to check profile:', error);
      } finally {
        setProfileCheckLoading(false);
      }
    };

    checkProfileCompletion();
  }, [user, location.pathname, profileSettings.require_profile_completion]);

  // Fetch firm info
  useEffect(() => {
    const fetchFirmInfo = async () => {
      if (user?.firm_id) {
        try {
          const response = await api.get('/firms/my/info');
          if (response.data.success && response.data.data) {
            setFirmInfo(response.data.data);
          }
        } catch (error) {
          console.error('Failed to fetch firm info', error);
        }
      }
    };
    fetchFirmInfo();
  }, [user?.firm_id]);

  // Fetch profile photo. We read /documents/my (not /employee-profile/me's
  // profile_photo_id field) so that users without an employee_profile record
  // — like SUPER_ADMINs — still see their avatar everywhere the layout
  // renders it (header, sidebar footer). Single source of truth: the
  // PERSONAL 'profile_photo' document most recently uploaded BY the current
  // user (or assigned to them).
  //
  // NOTE on the SUPER_ADMIN ownership filter:
  //   `/documents/my` for SUPER_ADMIN returns every doc with
  //   `access_level: "all"` — that's correct for the global Documents tab,
  //   but it means *another* super admin's profile_photo (which defaults to
  //   `access_level: "all"`) leaks into this list and would otherwise win
  //   the `created_at` sort. We must restrict to docs the current user
  //   owns or is assigned to before picking "the most recent" photo.
  useEffect(() => {
    // Haz 14, 2026 — `/documents/my` SUPER_ADMIN için tüm `access_level=all`
    // dokümanları döndüren PAHALI bir endpoint. Layout effect bazı durumlarda
    // (user referansı yenilendiğinde, StrictMode dev, vb.) tekrar tetikleniyor
    // ve her sayfa geçişinde 2+ çağrıya yol açıyordu.
    //
    // Çözüm: sessionStorage tabanlı 5dk'lık cache. Aynı user için 5dk içinde
    // tekrar çağrı yapma. profile-photo-updated event'i veya visibility
    // değişikliği zorla refresh ettirir.
    const CACHE_TTL_MS = 5 * 60 * 1000;
    const cacheKeyId = `dlayout_photo_id_${user?.id || 'anon'}`;
    const cacheKeyTs = `dlayout_photo_ts_${user?.id || 'anon'}`;
    const state = { currentUrl: null };

    const downloadAndSet = async (docId) => {
      // Şub 27, 2026 — Direct CDN URL ile profil fotoğrafı anında yüklenir.
      // Fallback olarak blob (getPreviewUrl bunu içeride yapar).
      const { getPreviewUrl, revokePreviewUrl } = await import('../lib/documentPreview');
      const { url: nextUrl } = await getPreviewUrl(api, docId, true);
      if (!nextUrl) return;
      revokePreviewUrl(state.currentUrl);
      state.currentUrl = nextUrl;
      setProfilePhotoUrl(nextUrl);
    };

    const fetchProfilePhoto = async (force = false) => {
      if (!user) return;
      // Cache check — TTL içindeyse ve cache'de docId varsa onu kullan.
      try {
        const ts = parseInt(sessionStorage.getItem(cacheKeyTs) || '0', 10);
        if (!force && ts && Date.now() - ts < CACHE_TTL_MS) {
          const cachedId = sessionStorage.getItem(cacheKeyId);
          if (cachedId === 'none') { setProfilePhotoUrl(null); return; }
          if (cachedId) {
            // Cache hit — sadece blob'u indir (görsel yoksa). State sıfırsa
            // yeniden çek; aksi takdirde mevcut blob URL'i koru.
            if (!state.currentUrl) await downloadAndSet(cachedId);
            return;
          }
        }
      } catch { /* sessionStorage erişilemiyor — devam */ }

      try {
        const docsResponse = await api.get('/documents/my');
        const docs = Array.isArray(docsResponse.data)
          ? docsResponse.data
          : (docsResponse.data?.data?.documents || []);
        // Pick the MOST RECENT profile_photo OWNED by this user.
        const photoDocs = docs
          .filter((d) => d.document_type === 'profile_photo')
          .filter((d) => (
            d.uploaded_by === user.id ||
            (Array.isArray(d.assigned_to) && d.assigned_to.includes(user.id))
          ))
          .sort((a, b) => String(b.created_at || '').localeCompare(String(a.created_at || '')));
        const photoDoc = photoDocs[0];
        // Cache yaz — başarılı API çağrısı sonrası.
        try {
          sessionStorage.setItem(cacheKeyTs, String(Date.now()));
          sessionStorage.setItem(cacheKeyId, photoDoc?.id || 'none');
        } catch { /* sessionStorage erişilemiyor */ }
        if (!photoDoc) {
          if (state.currentUrl) {
            URL.revokeObjectURL(state.currentUrl);
            state.currentUrl = null;
          }
          setProfilePhotoUrl(null);
          return;
        }
        await downloadAndSet(photoDoc.id);
      } catch (error) {
        console.error('Failed to fetch profile photo', error);
      }
    };
    fetchProfilePhoto(false);

    // Profil sayfasından "fotoğraf güncellendi" event'i → cache invalide + zorla refresh.
    const onPhotoUpdated = () => {
      try {
        sessionStorage.removeItem(cacheKeyTs);
        sessionStorage.removeItem(cacheKeyId);
      } catch { /* noop */ }
      fetchProfilePhoto(true);
    };
    window.addEventListener('profile-photo-updated', onPhotoUpdated);

    // Sekme yeniden görünür hale geldiğinde cache TTL bittiyse refresh.
    // `focus` listener'ı kaldırıldı — intra-app link tıklamalarında
    // bilgisayar ekranı sekmesi odak kaybetmediği için gereksiz, üstelik
    // tarayıcı bazı durumlarda her route geçişinde focus event'i yolluyor.
    const onVisible = () => {
      if (document.visibilityState === 'visible') fetchProfilePhoto(false);
    };
    document.addEventListener('visibilitychange', onVisible);

    return () => {
      window.removeEventListener('profile-photo-updated', onPhotoUpdated);
      document.removeEventListener('visibilitychange', onVisible);
      if (state.currentUrl) URL.revokeObjectURL(state.currentUrl);
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.id]);

  useEffect(() => {
    if (user?.language && user.language !== language) {
      setLanguage(user.language);
    }
  }, [user, language, setLanguage]);

  // Idle timer - lock screen after inactivity (user preference > system setting > default 5)
  const idleMinutes = user?.idle_timeout_minutes || settings.idleTimeoutMinutes || 5;
  useEffect(() => {
    if (!user) return;
    const timeoutMs = idleMinutes * 60 * 1000;
    const resetTimer = () => {
      if (idleTimerRef.current) clearTimeout(idleTimerRef.current);
      idleTimerRef.current = setTimeout(() => {
        lockScreen(user, idleMinutes);
        useAuthStore.getState().clearTokens();
      }, timeoutMs);
    };
    const events = ['mousedown', 'mousemove', 'keydown', 'scroll', 'touchstart', 'click'];
    events.forEach(e => window.addEventListener(e, resetTimer, { passive: true }));
    resetTimer();

    // Expose manual lock for header/sidebar buttons
    window.__forceIdleLock = () => {
      lockScreen(user, idleMinutes);
      useAuthStore.getState().clearTokens();
    };

    return () => {
      events.forEach(e => window.removeEventListener(e, resetTimer));
      if (idleTimerRef.current) clearTimeout(idleTimerRef.current);
      delete window.__forceIdleLock;
    };
  }, [user, idleMinutes, lockScreen]);

  const handleManualLock = () => {
    if (!user) return;
    lockScreen(user, idleMinutes);
    useAuthStore.getState().clearTokens();
  };

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const handleLanguageChange = async (lang) => {
    setLanguage(lang);
    await updateLanguage(lang);
  };

  const getNavItems = () => {
    const role = user?.role;
    
    if (role === 'SUPER_ADMIN') {
      return [
        { icon: LayoutDashboard, label: t('common.dashboard'), path: '/dashboard' },
        { icon: CalendarDays, label: t('dailyStatus.title') || 'Çalışan Durumu', path: '/dashboard/daily-status-v2' },
        { icon: BarChart3, label: t('workStats.employeeStatistics') || 'Çalışan İstatistikleri', path: '/dashboard/admin/work-statistics' },
        { 
          icon: Truck, 
          label: t('nav.routes') || 'Routelar', 
          isGroup: true,
          groupKey: 'routes',
          children: [
            { icon: Truck, label: t('nav.allRoutes') || 'Tüm Routelar', path: '/dashboard/routes' },
            { icon: PieChart, label: t('nav.routeReports') || 'Route Raporları', path: '/dashboard/routes/reports' },
            { icon: Users, label: 'Şoför Analizi', path: '/dashboard/routes/drivers' },
            { icon: Fuel, label: t('nav.fuelReports') || 'Yakıt Raporu', path: '/dashboard/routes/fuel' },
            { icon: Fuel, label: 'Mazot Bildirimleri', path: '/dashboard/routes/fuel-submissions' },
            { icon: Settings2, label: t('customers.title'), path: '/dashboard/customers' },
          ]
        },
        {
          icon: CalendarDays,
          label: t('nav.planning') || 'Planlama',
          isGroup: true,
          groupKey: 'planning',
          children: [
            { icon: CalendarDays, label: 'Planlama Yap', path: '/dashboard/planning' },
            { icon: ClipboardList, label: 'Planlama Listele', path: '/dashboard/planning/list' },
            { icon: ClipboardCheck, label: 'Planlama Formları', path: '/dashboard/planning/forms' },
            { icon: PlayCircle, label: 'Start-Stop Kayıtları', path: '/dashboard/planning/start-stop' },
            { icon: CalendarIcon, label: 'Özet', path: '/dashboard/planning/summary' },
            { icon: Sparkles, label: 'Planning Board', path: '/dashboard/planning/board' },
          ]
        },
        { icon: Car, label: t('nav.vehicles') || 'Araçlarım', isGroup: true, groupKey: 'vehicles', children: [
          { icon: Car, label: 'Araç Listesi', path: '/dashboard/vehicles' },
          { icon: ClipboardCheck, label: 'Araç Özeti & Atama', path: '/dashboard/vehicle-overview' },
          { icon: PieChart, label: 'Araç Raporu', path: '/dashboard/vehicles/reports' },
        ]},
        {
          icon: PostNLIcon,
          label: 'PostNL',
          isGroup: true,
          groupKey: 'postnl',
          children: [
            { icon: Package, label: t('packages.title') || 'Paketler', path: '/dashboard/packages' },
            { icon: PostNLIcon, label: 'PostNL Distributielijsten', path: '/dashboard/postnl-distribution' },
            { icon: PostNLIcon, label: t('nav.postnlCodes') || 'PostNL Kodları', path: '/dashboard/postnl-codes' },
            { icon: PostNLIcon, label: 'Dagplanning', path: '/dashboard/postnl/dagplanning' },
            { icon: PostNLIcon, label: 'Ritmonitor', path: '/dashboard/postnl/ritmonitor' },
            { icon: PostNLIcon, label: 'PostNL Hesapları', path: '/dashboard/postnl/accounts' },
          ]
        },
        {
          icon: Truck,
          label: t('nav.dpd') || 'DPD',
          isGroup: true,
          groupKey: 'dpd',
          children: [
            { icon: Truck, label: t('nav.dpdDailySummary') || 'DPD Günlük Özet', path: '/dashboard/dpd/summary' },
            { icon: TrendingUp, label: t('nav.dpdDriverPerformance') || 'DPD Şoför Performansı', path: '/dashboard/dpd/performance' },
            { icon: ClipboardList, label: t('nav.dpdConsolidated') || 'DPD Konsolide Rapor', path: '/dashboard/dpd/consolidated' },
            { icon: Receipt, label: t('nav.dpdMonthlyInvoice') || 'DPD Aylık Fatura', path: '/dashboard/dpd/invoice' },
            { icon: Flame, label: t('nav.dpdRetourHeatmap') || 'DPD Retour Isı Haritası', path: '/dashboard/dpd/heatmap' },
            { icon: GitCompare, label: t('nav.dpdSubcoComparison') || 'DPD Subco Karşılaştırma', path: '/dashboard/dpd/subco' },
            { icon: UserCheck, label: t('nav.dpdCourierMapping') || 'DPD Kurye Eşleştirme', path: '/dashboard/dpd/couriers' },
            { icon: ArrowLeftRight, label: t('nav.dpdRetourReport') || 'DPD Retour Raporu', path: '/dashboard/dpd/retour' },
            { icon: FolderTree, label: t('nav.dpdFileCategories') || 'DPD Dosya Kategorileri', path: '/dashboard/dpd/files' },
            { icon: Sparkles, label: t('nav.dpdAnalytics') || 'DPD Analitik & İçgörü', path: '/dashboard/dpd/analytics' },
            { icon: FileX, label: 'DPD Eksik Dosyalar', path: '/dashboard/dpd/missing' },
          ]
        },
        { 
          icon: Users, 
          label: t('common.users'), 
          isGroup: true,
          groupKey: 'users',
          children: [
            { icon: Users, label: t('nav.allUsers') || 'Tüm Kullanıcılar', path: '/dashboard/users' },
            { icon: User, label: t('employeeProfile.title') || 'Çalışan Profilleri', path: '/dashboard/employees' },
            { icon: ClipboardCheck, label: 'Çalışan Evrak Özeti', path: '/dashboard/employee-overview' },
            { icon: Activity, label: 'Personel Özet', path: '/dashboard/admin/employee-summary' },
            { icon: Briefcase, label: 'İş Başvuruları', path: '/dashboard/job-applications' },
          ]
        },
        { 
          icon: Calendar, 
          label: t('admin.leaveRequestManagement'), 
          isGroup: true,
          groupKey: 'leave',
          children: [
            { icon: ClipboardList, label: t('admin.allRequests') || 'Tüm Talepler', path: '/dashboard/admin/leave-requests' },
            { icon: ListChecks, label: t('leaveTypes.title') || 'İzin Türleri', path: '/dashboard/admin/leave-types' },
            { icon: CalendarOff, label: t('nav.lockedDates') || 'Kilitli Tarihler', path: '/dashboard/locked-dates' },
          ]
        },
        {
          icon: FolderOpen,
          label: t('nav.docManagement') || 'Dokümanlar',
          isGroup: true,
          groupKey: 'documents',
          children: [
            { icon: FolderOpen, label: t('nav.documentManagement') || 'Doküman Yönetimi', path: '/dashboard/documents' },
            { icon: FileText, label: t('nav.loonbrief') || 'Bordro Yükleme', path: '/dashboard/loonbrief' },
            { icon: List, label: t('nav.loonbriefList') || 'Bordro Listesi', path: '/dashboard/loonbrief/list' },
          ]
        },
        { icon: ClipboardList, label: t('nav.todoList') || 'Yapılacaklar', path: '/dashboard/todo' },
        {
          icon: Mail,
          label: t('nav.communication') || 'İletişim',
          isGroup: true,
          groupKey: 'communication',
          // Combined inbox badge so the collapsed parent still reflects unread.
          badge: (unreadMessages + unreadChats) > 0 ? (unreadMessages + unreadChats) : null,
          children: [
            { icon: Mail, label: t('nav.messages') || 'Mesajlar', path: '/dashboard/messages',
              badge: unreadMessages > 0 ? unreadMessages : null },
            { icon: MessageSquare, label: t('nav.chat') || 'Sohbet', path: '/dashboard/chat',
              badge: unreadChats > 0 ? unreadChats : null },
            { icon: Inbox, label: 'Mail (IMAP)', path: '/dashboard/mail' },
          ],
        },
        { 
          icon: Settings, 
          label: t('common.settings') || 'Ayarlar', 
          isGroup: true,
          groupKey: 'settings',
          children: [
            { icon: Building2, label: t('common.firms'), path: '/dashboard/firms' },
            { icon: Bell, label: t('notifications.title') || 'Bildirimler', path: '/dashboard/notifications' },
            { icon: Send, label: t('nav.sendNotification') || 'Bildirim Gönder', path: '/dashboard/send-notification' },
            { icon: Upload, label: t('nav.pdfUpload') || 'PDF Yükle', href: 'https://pdf.hyk.be/login?pw=SFlLMjAyNVNlY3VyZQ==', external: true },
            { icon: FileText, label: t('common.auditLogs') || 'Denetim Kayıtları', path: '/dashboard/audit' },
            { icon: Globe, label: t('nav.frontendSettings') || 'Görünüm Ayarları', path: '/dashboard/frontend-settings' },
            { icon: ServerCog, label: t('nav.siteSettings') || 'Site Ayarları', path: '/dashboard/site-settings' },
            { icon: Languages, label: t('nav.languages') || 'Diller', path: '/dashboard/languages' },
            { icon: Database, label: t('nav.databaseBackup') || 'Veritabanı Yedekleme', path: '/dashboard/database-backup' },
            { icon: Clock, label: 'Cron / Zamanlı Görevler', path: '/dashboard/settings/cron-jobs' },
            { icon: Inbox, label: 'Mail Hesapları (IMAP)', path: '/dashboard/settings/mail-accounts' },
            { icon: FileText, label: 'Mail Arşiv Kuralları', path: '/dashboard/settings/mail-rules' },
            { icon: Archive, label: 'Mail Arşivi', path: '/dashboard/settings/mail-archive' },
            { icon: Building2, label: 'Depo Listesi', path: '/dashboard/settings/depots' },
            { icon: ClipboardCheck, label: 'Depo Form Şablonları', path: '/dashboard/settings/depot-forms' },
            { icon: MapIcon, label: 'Harita Ayarları', path: '/dashboard/settings/map' },
            { icon: Flag, label: 'Feature Flags', path: '/dashboard/settings/feature-flags' },
            { icon: Cloud, label: 'Depolama (S3)', path: '/dashboard/settings/storage' },
            { icon: HardDrive, label: 'Dosya Envanteri', path: '/dashboard/settings/storage-inventory' },
            { icon: Database, label: 'Mongo Sağlık', path: '/dashboard/settings/mongo-health' },
          ]
        },
      ];
    } else if (role === 'FULL_ADMIN') {
      // FULL_ADMIN: Same as FIRM_ADMIN but filtered by allowed_pages from permissions
      const allPages = [
        { icon: LayoutDashboard, label: t('common.dashboard'), path: '/dashboard', pageKey: 'dashboard' },
        // Kişisel çalışan menüsü (Haz 13, 2026 — grup haline getirildi)
        // optIn: yetki boş olduğunda grup tamamen gizli. pageKey: 'employee-self-view'
        // grubu ve tüm children'ı bir arada açar/kapatır.
        {
          icon: User,
          label: t('nav.personal') || 'Kişisel',
          isGroup: true,
          groupKey: 'personal',
          pageKey: 'employee-self-view',
          optIn: true,
          children: [
            { icon: BarChart3, label: t('workStats.myStatistics') || 'Çalışma Durumum', path: '/dashboard/work-statistics' },
            { icon: FileText, label: t('nav.myPayslips') || 'Bodrolarım', path: '/dashboard/my-payslips' },
            { icon: Package, label: t('nav.myPackages') || 'Paketlerim', path: '/dashboard/my-packages' },
            // Şub 16, 2026 — Feature Flag ile gate'lenir. Admin panelinden açılınca görünür.
            ...(canEmployeeFinance ? [{ icon: Euro, label: t('nav.myFinance') || 'Finansal Verilerim', path: '/dashboard/my-finance' }] : []),
            { icon: ClipboardList, label: t('common.leaveRequests') || 'İzin Talepleri', path: '/dashboard/leave-requests' },
            { icon: FolderOpen, label: t('nav.myDocuments') || 'Dosyalarım', path: '/dashboard/my-documents' },
            { icon: Bell, label: t('nav.myNotifications') || 'Bildirimlerim', path: '/dashboard/my-notifications' },
            { icon: User, label: t('employeeProfile.myProfile') || 'Profilim', path: '/dashboard/my-profile' },
          ],
        },
        { icon: CalendarDays, label: t('dailyStatus.title') || 'Çalışan Durumu', path: '/dashboard/daily-status-v2', pageKey: 'daily-status' },
        { icon: BarChart3, label: t('workStats.employeeStatistics') || 'Çalışan İstatistikleri', path: '/dashboard/admin/work-statistics', pageKey: 'work-statistics' },
        { 
          icon: Truck, 
          label: t('nav.routes') || 'Routelar', 
          isGroup: true,
          groupKey: 'routes',
          pageKey: ['routes', 'routes-reports', 'routes-drivers', 'routes-fuel', 'fuel-submissions', 'customers'],
          children: [
            { icon: Truck, label: t('nav.allRoutes') || 'Tüm Routelar', path: '/dashboard/routes', pageKey: 'routes' },
            { icon: PieChart, label: t('nav.routeReports') || 'Route Raporları', path: '/dashboard/routes/reports', pageKey: 'routes-reports' },
            { icon: Users, label: 'Şoför Analizi', path: '/dashboard/routes/drivers', pageKey: 'routes-drivers' },
            { icon: Fuel, label: t('nav.fuelReports') || 'Yakıt Raporu', path: '/dashboard/routes/fuel', pageKey: 'routes-fuel' },
            { icon: Fuel, label: 'Mazot Bildirimleri', path: '/dashboard/routes/fuel-submissions', pageKey: 'fuel-submissions' },
            { icon: Settings2, label: t('customers.title'), path: '/dashboard/customers', pageKey: 'customers', optIn: true },
          ]
        },
        {
          icon: Car, label: t('nav.vehicles') || 'Araçlarım', isGroup: true, groupKey: 'vehicles',
          pageKey: ['vehicles', 'vehicle-overview', 'vehicle-reports'],
          children: [
            { icon: Car, label: 'Araç Listesi', path: '/dashboard/vehicles', pageKey: 'vehicles' },
            { icon: ClipboardCheck, label: 'Araç Özeti & Atama', path: '/dashboard/vehicle-overview', pageKey: 'vehicle-overview', optIn: true },
            { icon: PieChart, label: 'Araç Raporu', path: '/dashboard/vehicles/reports', pageKey: 'vehicle-reports', optIn: true },
          ]
        },
        {
          icon: CalendarDays,
          label: t('nav.planning') || 'Planlama',
          isGroup: true,
          groupKey: 'planning',
          pageKey: ['planning', 'planning-edit', 'planning-list', 'planning-forms', 'planning-start-stop', 'planning-summary', 'planning-board'],
          children: [
            { icon: CalendarDays, label: 'Planlama Yap', path: '/dashboard/planning', pageKey: ['planning', 'planning-edit'] },
            { icon: ClipboardList, label: 'Planlama Listele', path: '/dashboard/planning/list', pageKey: 'planning-list' },
            { icon: ClipboardCheck, label: 'Planlama Formları', path: '/dashboard/planning/forms', pageKey: 'planning-forms' },
            { icon: PlayCircle, label: 'Start-Stop Kayıtları', path: '/dashboard/planning/start-stop', pageKey: 'planning-start-stop' },
            { icon: CalendarIcon, label: 'Özet', path: '/dashboard/planning/summary', pageKey: 'planning-summary' },
            { icon: Sparkles, label: 'Planning Board', path: '/dashboard/planning/board', pageKey: 'planning-board' },
          ]
        },
        { 
          icon: Users, 
          label: t('common.users'), 
          isGroup: true,
          groupKey: 'users-group',
          pageKey: ['users', 'employees', 'employee-overview', 'employee-summary', 'job-applications'],
          children: [
            { icon: Users, label: t('nav.allUsers') || 'Kullanıcı Yönetimi', path: '/dashboard/users', pageKey: 'users' },
            { icon: User, label: t('employeeProfile.title') || 'Çalışan Profilleri', path: '/dashboard/employees', pageKey: 'employees' },
            { icon: ClipboardCheck, label: 'Çalışan Evrak Özeti', path: '/dashboard/employee-overview', pageKey: 'employee-overview' },
            { icon: Activity, label: 'Personel Özet', path: '/dashboard/admin/employee-summary', pageKey: 'employee-summary', optIn: true },
            { icon: Briefcase, label: 'İş Başvuruları', path: '/dashboard/job-applications', pageKey: 'job-applications' },
          ]
        },
        { 
          icon: Calendar, 
          label: t('admin.leaveManagement') || t('admin.leaveRequestManagement'), 
          isGroup: true,
          groupKey: 'leave',
          pageKey: 'leave',
          children: [
            { icon: ClipboardList, label: t('admin.allRequests') || 'Tüm Talepler', path: '/dashboard/admin/leave-requests' },
            { icon: ClipboardList, label: t('common.leaveRequests') || 'İzin Taleplerim', path: '/dashboard/leave-requests' },
            { icon: CalendarOff, label: t('nav.lockedDates') || 'Kilitli Tarihler', path: '/dashboard/locked-dates' },
          ]
        },
        { icon: Eye, label: t('employeeTracking.title') || 'Çalışan Takibi', path: '/dashboard/employee-tracking', pageKey: 'employee-tracking' },
        // Şub 16, 2026 — PostNL menüsü FIRM_ADMIN/FULL_ADMIN için pageKey ile gate'lenir
        {
          icon: PostNLIcon,
          label: 'PostNL',
          isGroup: true,
          groupKey: 'postnl',
          pageKey: ['postnl-dagplanning', 'postnl-ritmonitor', 'postnl-codes', 'postnl-credentials', 'postnl-distribution'],
          children: [
            { icon: PostNLIcon, label: 'PostNL Distributielijsten', path: '/dashboard/postnl-distribution', pageKey: 'postnl-distribution', optIn: true },
            { icon: PostNLIcon, label: t('nav.postnlCodes') || 'PostNL Kodları', path: '/dashboard/postnl-codes', pageKey: 'postnl-codes' },
            { icon: PostNLIcon, label: 'Dagplanning', path: '/dashboard/postnl/dagplanning', pageKey: 'postnl-dagplanning' },
            { icon: PostNLIcon, label: 'Ritmonitor', path: '/dashboard/postnl/ritmonitor', pageKey: 'postnl-ritmonitor' },
            { icon: PostNLIcon, label: 'PostNL Hesapları', path: '/dashboard/postnl/accounts', pageKey: 'postnl-credentials' },
          ]
        },
        // Şub 27, 2026 — DPD menüsü FIRM_ADMIN/FULL_ADMIN için pageKey ile gate'lenir
        {
          icon: Truck,
          label: t('nav.dpd') || 'DPD',
          isGroup: true,
          groupKey: 'dpd',
          pageKey: ['dpd-summary','dpd-performance','dpd-consolidated','dpd-invoice','dpd-heatmap','dpd-subco','dpd-couriers','dpd-retour','dpd-files','dpd-analytics','dpd-missing'],
          children: [
            { icon: Truck, label: t('nav.dpdDailySummary') || 'DPD Günlük Özet', path: '/dashboard/dpd/summary', pageKey: 'dpd-summary' },
            { icon: TrendingUp, label: t('nav.dpdDriverPerformance') || 'DPD Şoför Performansı', path: '/dashboard/dpd/performance', pageKey: 'dpd-performance' },
            { icon: ClipboardList, label: t('nav.dpdConsolidated') || 'DPD Konsolide Rapor', path: '/dashboard/dpd/consolidated', pageKey: 'dpd-consolidated' },
            { icon: Receipt, label: t('nav.dpdMonthlyInvoice') || 'DPD Aylık Fatura', path: '/dashboard/dpd/invoice', pageKey: 'dpd-invoice' },
            { icon: Flame, label: t('nav.dpdRetourHeatmap') || 'DPD Retour Isı Haritası', path: '/dashboard/dpd/heatmap', pageKey: 'dpd-heatmap' },
            { icon: GitCompare, label: t('nav.dpdSubcoComparison') || 'DPD Subco Karşılaştırma', path: '/dashboard/dpd/subco', pageKey: 'dpd-subco' },
            { icon: UserCheck, label: t('nav.dpdCourierMapping') || 'DPD Kurye Eşleştirme', path: '/dashboard/dpd/couriers', pageKey: 'dpd-couriers' },
            { icon: ArrowLeftRight, label: t('nav.dpdRetourReport') || 'DPD Retour Raporu', path: '/dashboard/dpd/retour', pageKey: 'dpd-retour' },
            { icon: FolderTree, label: t('nav.dpdFileCategories') || 'DPD Dosya Kategorileri', path: '/dashboard/dpd/files', pageKey: 'dpd-files' },
            { icon: Sparkles, label: t('nav.dpdAnalytics') || 'DPD Analitik & İçgörü', path: '/dashboard/dpd/analytics', pageKey: 'dpd-analytics' },
            { icon: FileX, label: 'DPD Eksik Dosyalar', path: '/dashboard/dpd/missing', pageKey: 'dpd-missing', optIn: true },
          ]
        },
        {
          icon: FolderOpen,
          label: t('nav.docManagement') || 'Dokümanlar',
          isGroup: true,
          groupKey: 'documents-group',
          pageKey: ['documents', 'loonbrief', 'loonbrief-list'],
          children: [
            { icon: FolderOpen, label: t('nav.documentManagement') || 'Doküman Yönetimi', path: '/dashboard/documents', pageKey: 'documents' },
            { icon: FileText, label: t('nav.loonbrief') || 'Bordro Yükleme', path: '/dashboard/loonbrief', pageKey: 'loonbrief' },
            { icon: List, label: t('nav.loonbriefList') || 'Bordro Listesi', path: '/dashboard/loonbrief/list', pageKey: 'loonbrief-list' },
          ]
        },
        { 
          icon: Settings, 
          label: t('common.settings') || 'Ayarlar', 
          isGroup: true,
          groupKey: 'settings',
          pageKey: 'settings',
          children: [
            { icon: Bell, label: t('notifications.title') || 'Bildirimler', path: '/dashboard/notifications' },
            ...(user?.permissions?.can_send_notifications ? [{ icon: Send, label: t('nav.sendNotification') || 'Bildirim Gönder', path: '/dashboard/send-notification' }] : []),
            { icon: FileText, label: t('common.auditLogs') || 'Denetim Kayıtları', path: '/dashboard/audit' },
          ]
        },
      ];
      return filterMenuByPages(allPages, user?.permissions?.allowed_pages || []);
    } else if (role === 'FIRM_ADMIN') {
      // FIRM_ADMIN: Apply page permissions if set
      const firmAdminPages = [
        { icon: LayoutDashboard, label: t('common.dashboard'), path: '/dashboard', pageKey: 'dashboard' },
        // Kişisel çalışan menüsü — grup haline getirildi (Haz 13, 2026)
        {
          icon: User,
          label: t('nav.personal') || 'Kişisel',
          isGroup: true,
          groupKey: 'personal',
          pageKey: 'employee-self-view',
          optIn: true,
          children: [
            { icon: BarChart3, label: t('workStats.myStatistics') || 'Çalışma Durumum', path: '/dashboard/work-statistics' },
            { icon: FileText, label: t('nav.myPayslips') || 'Bodrolarım', path: '/dashboard/my-payslips' },
            { icon: Package, label: t('nav.myPackages') || 'Paketlerim', path: '/dashboard/my-packages' },
            // Şub 16, 2026 — Feature Flag ile gate'lenir. Admin panelinden açılınca görünür.
            ...(canEmployeeFinance ? [{ icon: Euro, label: t('nav.myFinance') || 'Finansal Verilerim', path: '/dashboard/my-finance' }] : []),
            { icon: ClipboardList, label: t('common.leaveRequests') || 'İzin Talepleri', path: '/dashboard/leave-requests' },
            { icon: FolderOpen, label: t('nav.myDocuments') || 'Dosyalarım', path: '/dashboard/my-documents' },
            { icon: Bell, label: t('nav.myNotifications') || 'Bildirimlerim', path: '/dashboard/my-notifications' },
            { icon: User, label: t('employeeProfile.myProfile') || 'Profilim', path: '/dashboard/my-profile' },
          ],
        },
        { icon: CalendarDays, label: t('dailyStatus.title') || 'Çalışan Durumu', path: '/dashboard/daily-status-v2', pageKey: 'daily-status' },
        { icon: BarChart3, label: t('workStats.employeeStatistics') || 'Çalışan İstatistikleri', path: '/dashboard/admin/work-statistics', pageKey: 'work-statistics' },
        { 
          icon: Truck, 
          label: t('nav.routes') || 'Routelar', 
          isGroup: true,
          groupKey: 'routes',
          pageKey: ['routes', 'routes-reports', 'routes-drivers', 'routes-fuel', 'fuel-submissions', 'customers'],
          children: [
            { icon: Truck, label: t('nav.allRoutes') || 'Tüm Routelar', path: '/dashboard/routes', pageKey: 'routes' },
            { icon: PieChart, label: t('nav.routeReports') || 'Route Raporları', path: '/dashboard/routes/reports', pageKey: 'routes-reports' },
            { icon: Users, label: 'Şoför Analizi', path: '/dashboard/routes/drivers', pageKey: 'routes-drivers' },
            { icon: Fuel, label: t('nav.fuelReports') || 'Yakıt Raporu', path: '/dashboard/routes/fuel', pageKey: 'routes-fuel' },
            { icon: Fuel, label: 'Mazot Bildirimleri', path: '/dashboard/routes/fuel-submissions', pageKey: 'fuel-submissions' },
            { icon: Settings2, label: t('customers.title'), path: '/dashboard/customers', pageKey: 'customers', optIn: true },
          ]
        },
        {
          icon: Car, label: t('nav.vehicles') || 'Araçlarım', isGroup: true, groupKey: 'vehicles',
          pageKey: ['vehicles', 'vehicle-overview', 'vehicle-reports'],
          children: [
            { icon: Car, label: 'Araç Listesi', path: '/dashboard/vehicles', pageKey: 'vehicles' },
            { icon: ClipboardCheck, label: 'Araç Özeti & Atama', path: '/dashboard/vehicle-overview', pageKey: 'vehicle-overview', optIn: true },
            { icon: PieChart, label: 'Araç Raporu', path: '/dashboard/vehicles/reports', pageKey: 'vehicle-reports', optIn: true },
          ]
        },
        {
          icon: CalendarDays,
          label: t('nav.planning') || 'Planlama',
          isGroup: true,
          groupKey: 'planning',
          pageKey: ['planning', 'planning-edit', 'planning-list', 'planning-forms', 'planning-start-stop', 'planning-summary', 'planning-board'],
          children: [
            { icon: CalendarDays, label: 'Planlama Yap', path: '/dashboard/planning', pageKey: ['planning', 'planning-edit'] },
            { icon: ClipboardList, label: 'Planlama Listele', path: '/dashboard/planning/list', pageKey: 'planning-list' },
            { icon: ClipboardCheck, label: 'Planlama Formları', path: '/dashboard/planning/forms', pageKey: 'planning-forms' },
            { icon: PlayCircle, label: 'Start-Stop Kayıtları', path: '/dashboard/planning/start-stop', pageKey: 'planning-start-stop' },
            { icon: CalendarIcon, label: 'Özet', path: '/dashboard/planning/summary', pageKey: 'planning-summary' },
            { icon: Sparkles, label: 'Planning Board', path: '/dashboard/planning/board', pageKey: 'planning-board' },
          ]
        },
        { 
          icon: Users, 
          label: t('common.users'), 
          isGroup: true,
          groupKey: 'users-group',
          pageKey: ['users', 'employees', 'employee-overview', 'employee-summary', 'job-applications'],
          children: [
            { icon: Users, label: t('nav.allUsers') || 'Kullanıcı Yönetimi', path: '/dashboard/users', pageKey: 'users' },
            { icon: User, label: t('employeeProfile.title') || 'Çalışan Profilleri', path: '/dashboard/employees', pageKey: 'employees' },
            { icon: ClipboardCheck, label: 'Çalışan Evrak Özeti', path: '/dashboard/employee-overview', pageKey: 'employee-overview' },
            { icon: Activity, label: 'Personel Özet', path: '/dashboard/admin/employee-summary', pageKey: 'employee-summary', optIn: true },
            { icon: Briefcase, label: 'İş Başvuruları', path: '/dashboard/job-applications', pageKey: 'job-applications' },
          ]
        },
        { 
          icon: Calendar, 
          label: t('admin.leaveManagement') || t('admin.leaveRequestManagement'), 
          isGroup: true,
          groupKey: 'leave',
          pageKey: 'leave',
          children: [
            { icon: ClipboardList, label: t('admin.allRequests') || 'Tüm Talepler', path: '/dashboard/admin/leave-requests' },
            { icon: ClipboardList, label: t('common.leaveRequests') || 'İzin Taleplerim', path: '/dashboard/leave-requests' },
            { icon: CalendarOff, label: t('nav.lockedDates') || 'Kilitli Tarihler', path: '/dashboard/locked-dates' },
          ]
        },
        { icon: Eye, label: t('employeeTracking.title') || 'Çalışan Takibi', path: '/dashboard/employee-tracking', pageKey: 'employee-tracking' },
        // Şub 16, 2026 — PostNL menüsü FIRM_ADMIN/FULL_ADMIN için pageKey ile gate'lenir
        {
          icon: PostNLIcon,
          label: 'PostNL',
          isGroup: true,
          groupKey: 'postnl',
          pageKey: ['postnl-dagplanning', 'postnl-ritmonitor', 'postnl-codes', 'postnl-credentials', 'postnl-distribution'],
          children: [
            { icon: PostNLIcon, label: 'PostNL Distributielijsten', path: '/dashboard/postnl-distribution', pageKey: 'postnl-distribution', optIn: true },
            { icon: PostNLIcon, label: t('nav.postnlCodes') || 'PostNL Kodları', path: '/dashboard/postnl-codes', pageKey: 'postnl-codes' },
            { icon: PostNLIcon, label: 'Dagplanning', path: '/dashboard/postnl/dagplanning', pageKey: 'postnl-dagplanning' },
            { icon: PostNLIcon, label: 'Ritmonitor', path: '/dashboard/postnl/ritmonitor', pageKey: 'postnl-ritmonitor' },
            { icon: PostNLIcon, label: 'PostNL Hesapları', path: '/dashboard/postnl/accounts', pageKey: 'postnl-credentials' },
          ]
        },
        // Şub 27, 2026 — DPD menüsü FIRM_ADMIN/FULL_ADMIN için pageKey ile gate'lenir
        {
          icon: Truck,
          label: t('nav.dpd') || 'DPD',
          isGroup: true,
          groupKey: 'dpd',
          pageKey: ['dpd-summary','dpd-performance','dpd-consolidated','dpd-invoice','dpd-heatmap','dpd-subco','dpd-couriers','dpd-retour','dpd-files','dpd-analytics','dpd-missing'],
          children: [
            { icon: Truck, label: t('nav.dpdDailySummary') || 'DPD Günlük Özet', path: '/dashboard/dpd/summary', pageKey: 'dpd-summary' },
            { icon: TrendingUp, label: t('nav.dpdDriverPerformance') || 'DPD Şoför Performansı', path: '/dashboard/dpd/performance', pageKey: 'dpd-performance' },
            { icon: ClipboardList, label: t('nav.dpdConsolidated') || 'DPD Konsolide Rapor', path: '/dashboard/dpd/consolidated', pageKey: 'dpd-consolidated' },
            { icon: Receipt, label: t('nav.dpdMonthlyInvoice') || 'DPD Aylık Fatura', path: '/dashboard/dpd/invoice', pageKey: 'dpd-invoice' },
            { icon: Flame, label: t('nav.dpdRetourHeatmap') || 'DPD Retour Isı Haritası', path: '/dashboard/dpd/heatmap', pageKey: 'dpd-heatmap' },
            { icon: GitCompare, label: t('nav.dpdSubcoComparison') || 'DPD Subco Karşılaştırma', path: '/dashboard/dpd/subco', pageKey: 'dpd-subco' },
            { icon: UserCheck, label: t('nav.dpdCourierMapping') || 'DPD Kurye Eşleştirme', path: '/dashboard/dpd/couriers', pageKey: 'dpd-couriers' },
            { icon: ArrowLeftRight, label: t('nav.dpdRetourReport') || 'DPD Retour Raporu', path: '/dashboard/dpd/retour', pageKey: 'dpd-retour' },
            { icon: FolderTree, label: t('nav.dpdFileCategories') || 'DPD Dosya Kategorileri', path: '/dashboard/dpd/files', pageKey: 'dpd-files' },
            { icon: Sparkles, label: t('nav.dpdAnalytics') || 'DPD Analitik & İçgörü', path: '/dashboard/dpd/analytics', pageKey: 'dpd-analytics' },
            { icon: FileX, label: 'DPD Eksik Dosyalar', path: '/dashboard/dpd/missing', pageKey: 'dpd-missing', optIn: true },
          ]
        },
        {
          icon: FolderOpen,
          label: t('nav.docManagement') || 'Dokümanlar',
          isGroup: true,
          groupKey: 'documents-group',
          pageKey: ['documents', 'loonbrief', 'loonbrief-list'],
          children: [
            { icon: FolderOpen, label: t('nav.documentManagement') || 'Doküman Yönetimi', path: '/dashboard/documents', pageKey: 'documents' },
            { icon: FileText, label: t('nav.loonbrief') || 'Bordro Yükleme', path: '/dashboard/loonbrief', pageKey: 'loonbrief' },
            { icon: List, label: t('nav.loonbriefList') || 'Bordro Listesi', path: '/dashboard/loonbrief/list', pageKey: 'loonbrief-list' },
          ]
        },
        {
          icon: Mail,
          label: t('nav.communication') || 'İletişim',
          isGroup: true,
          groupKey: 'communication',
          pageKey: ['messages', 'chat'],
          children: [
            { icon: Mail, label: t('nav.messages') || 'Mesajlar', path: '/dashboard/messages', pageKey: 'messages' },
            { icon: MessageSquare, label: t('nav.chat') || 'Sohbet', path: '/dashboard/chat', pageKey: 'chat' },
          ],
        },
        { 
          icon: Settings, 
          label: t('common.settings') || 'Ayarlar', 
          isGroup: true,
          groupKey: 'settings',
          pageKey: 'settings',
          children: [
            { icon: Bell, label: t('notifications.title') || 'Bildirimler', path: '/dashboard/notifications' },
            { icon: Send, label: t('nav.sendNotification') || 'Bildirim Gönder', path: '/dashboard/send-notification' },
            { icon: Upload, label: t('nav.pdfUpload') || 'PDF Yükle', href: 'https://pdf.hyk.be/login?pw=SFlLMjAyNVNlY3VyZQ==', external: true },
            { icon: FileText, label: t('common.auditLogs') || 'Denetim Kayıtları', path: '/dashboard/audit' },
          ]
        },
      ];
      return filterMenuByPages(firmAdminPages, user?.permissions?.allowed_pages || []);
    } else if (role === 'SUBCO') {
      // Mart 01, 2026 — SUBCO (Alt Taşeron) sadeleştirilmiş menü
      // Subco kullanıcı sisteme girdiğinde sadece atanmış planlamalarını görür.
      return [
        { icon: LayoutDashboard, label: t('common.dashboard'), path: '/dashboard' },
        { icon: Truck, label: 'Planlamalarım', path: '/dashboard/my-routes' },
        { icon: Users, label: 'Çalışanlarım', path: '/dashboard/my-subco-employees' },
      ];
    } else {
      const employeeItems = [
        { icon: LayoutDashboard, label: t('common.dashboard'), path: '/dashboard' },
        {
          icon: User,
          label: t('nav.personal') || 'Kişisel',
          isGroup: true,
          groupKey: 'personal',
          children: [
            { icon: BarChart3, label: t('workStats.myStatistics') || 'Çalışma Durumum', path: '/dashboard/work-statistics' },
            { icon: FileText, label: t('nav.myPayslips') || 'Bodrolarım', path: '/dashboard/my-payslips' },
            { icon: Package, label: t('nav.myPackages') || 'Paketlerim', path: '/dashboard/my-packages' },
            // Şub 16, 2026 — Feature Flag ile gate'lenir. Admin panelinden açılınca görünür.
            ...(canEmployeeFinance ? [{ icon: Euro, label: t('nav.myFinance') || 'Finansal Verilerim', path: '/dashboard/my-finance' }] : []),
            { icon: ClipboardList, label: t('common.leaveRequests'), path: '/dashboard/leave-requests' },
            { icon: FolderOpen, label: t('nav.myDocuments') || 'Dosyalarım', path: '/dashboard/my-documents' },
            { icon: Bell, label: t('nav.myNotifications') || 'Bildirimlerim', path: '/dashboard/my-notifications', badge: unreadNotifications },
            { icon: User, label: t('employeeProfile.myProfile') || 'Profilim', path: '/dashboard/my-profile' },
          ],
        },
      ];

      employeeItems.push(
        {
          icon: Mail,
          label: t('nav.communication') || 'İletişim',
          isGroup: true,
          groupKey: 'communication',
          children: [
            { icon: Mail, label: t('nav.messages') || 'Mesajlar', path: '/dashboard/messages' },
            { icon: MessageSquare, label: t('nav.chat') || 'Sohbet', path: '/dashboard/chat' },
          ],
        },
      );
      
      return employeeItems;
    }
  };

  const navItems = getNavItems();
  navItemsRef.current = navItems;
  const favCatalogKey = navItems.map((i) => `${i.path || ''}${(i.children || []).map((c) => c.path).join(',')}`).join('|');
  useEffect(() => {
    if (!FAV_ROLES.includes(user?.role)) return;
    useFavorites.getState().setCatalog(flattenMenu(navItemsRef.current));
    useFavorites.getState().load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [favCatalogKey, user?.role]);

  return (
    <div className="min-h-screen bg-slate-50/50 dark:bg-slate-900" data-testid="dashboard-layout">
      {/* Push Notification Prompt */}
      <PushNotificationPrompt />
      
      {/* Mobile Sidebar Overlay */}
      {sidebarOpen && (
        <div 
          className="fixed inset-0 bg-black/50 z-40 lg:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Sidebar */}
      <aside className={`fixed inset-y-0 left-0 z-50 sidebar-modern border-r border-slate-200/50 dark:border-slate-700/30 transform transition-all duration-300 lg:translate-x-0 ${sidebarOpen ? 'translate-x-0' : '-translate-x-full'} ${sidebarCollapsed ? 'lg:w-[72px]' : 'lg:w-64'} w-64`}>
        <div className="flex flex-col h-full">
          {/* Logo / Firm Info */}
          <div className={`flex items-center justify-between h-16 px-4 ${sidebarCollapsed ? '' : 'px-5'}`}>
            <Link to="/dashboard" className={`flex items-center gap-3 flex-1 min-w-0 ${sidebarCollapsed ? 'lg:justify-center' : ''}`}>
              {firmInfo?.logo ? (
                <img 
                  src={firmInfo.logo} 
                  alt={firmInfo.name} 
                  className="h-9 w-9 rounded-xl object-cover flex-shrink-0 ring-1 ring-black/5 dark:ring-white/10"
                />
              ) : (
                <div className="h-9 w-9 rounded-xl bg-gradient-to-br from-blue-600 to-indigo-600 flex items-center justify-center flex-shrink-0 shadow-sm shadow-blue-500/20">
                  <Building2 className="h-5 w-5 text-white" />
                </div>
              )}
              {/* Firma adı / OMS Portal — collapse SADECE desktop'ta gizler;
                  mobile drawer açıkken her zaman görünür. */}
              <div className={`min-w-0 flex-1 ${sidebarCollapsed ? 'lg:hidden' : ''}`}>
                <span className="font-bold text-sm text-slate-800 dark:text-slate-100 truncate block" style={{ fontFamily: 'Manrope, sans-serif' }}>
                  {firmInfo?.name || settings.siteName || 'OMS Portal'}
                </span>
                {firmInfo && (
                  <span className="text-[10px] text-slate-400 dark:text-slate-500 truncate block">
                    {t('common.firm') || 'Firma'}
                  </span>
                )}
              </div>
            </Link>
            <button 
              className="lg:hidden p-1.5 hover:bg-slate-100 dark:hover:bg-slate-800 rounded-lg flex-shrink-0 transition-colors"
              onClick={() => setSidebarOpen(false)}
            >
              <X className="h-4 w-4 text-slate-400 dark:text-slate-500" />
            </button>
          </div>
          <div className="sidebar-group-divider" />

          {/* Navigation */}
          <nav ref={navRef} className={`flex-1 py-3 space-y-1 overflow-y-auto sidebar-nav ${sidebarCollapsed ? 'lg:px-2' : 'lg:px-3'} px-2`}>
            <TooltipProvider delayDuration={0}>
              {navItems.map((item) => {
                // Check if this is a group with children
                if (item.isGroup && item.children) {
                  const isAnyChildActive = item.children.some(child => 
                    location.pathname === child.path || location.pathname.startsWith(child.path)
                  );
                  
                  // Single-open accordion: group is expanded iff the shared
                  // `openGroupKey` matches ours. If the user navigated via a
                  // URL (deep-link) to a child route we also show it as
                  // expanded — discovered via `isAnyChildActive`.
                  const isExpanded = openGroupKey === item.groupKey || isAnyChildActive;
                  const toggleExpanded = (e) => {
                    const button = e.currentTarget;
                    const willOpen = openGroupKey !== item.groupKey;
                    setOpenGroupKey(willOpen ? item.groupKey : null);
                    if (willOpen && navRef.current) {
                      setTimeout(() => {
                        button.scrollIntoView({ behavior: 'smooth', block: 'start' });
                      }, 50);
                    }
                  };
                  
                  return (
                    <div
                      key={item.label}
                      className={`space-y-1 ${
                        isExpanded && !sidebarCollapsed
                          ? 'bg-indigo-50/60 dark:bg-indigo-950/30 rounded-lg px-1 py-1 -mx-1 ring-1 ring-indigo-100 dark:ring-indigo-900/40 transition-colors'
                          : ''
                      }`}
                    >
                      {/* Group divider */}
                      {!sidebarCollapsed && !isExpanded && <div className="sidebar-group-divider" />}
                      {/* Group Header */}
                      <button
                        onClick={toggleExpanded}
                        className={`sidebar-link w-full justify-between ${isAnyChildActive ? 'active' : ''} ${sidebarCollapsed ? 'lg:justify-center lg:px-0' : ''}`}
                        data-testid={`nav-${item.groupKey || 'settings'}-group`}
                      >
                        <div className="flex items-center gap-3 min-w-0 flex-1">
                          <item.icon className="h-[18px] w-[18px] flex-shrink-0" />
                          <span className={`truncate ${sidebarCollapsed ? 'lg:hidden' : ''}`}>{item.label}</span>
                        </div>
                        <ChevronRight className={`h-3.5 w-3.5 transition-transform duration-200 flex-shrink-0 ${isExpanded ? 'rotate-90' : ''} ${sidebarCollapsed ? 'lg:hidden' : ''}`} />
                      </button>
                      
                      {/* Children */}
                      {(isExpanded || (sidebarCollapsed && isDesktop)) && (
                        <div className={`space-y-0.5 ${sidebarCollapsed ? '' : 'ml-5 pl-2.5 border-l-2 border-indigo-300 dark:border-indigo-700/60'}`}>
                          {item.children.map((child) => {
                            // External link in children
                            if (child.external && child.href) {
                              const externalChildContent = (
                                <a
                                  key={child.href}
                                  href={child.href}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className={`sidebar-link text-[12px] py-1.5 ${sidebarCollapsed ? 'lg:justify-center lg:px-0' : ''}`}
                                  data-testid={`nav-external-${child.label.toLowerCase().replace(/\s+/g, '-')}`}
                                >
                                  <child.icon className="h-3.5 w-3.5 flex-shrink-0 opacity-70" />
                                  <span className={`truncate ${sidebarCollapsed ? 'lg:hidden' : ''}`}>
                                    {child.label}
                                    <ExternalLink className="h-2.5 w-2.5 ml-1 inline-block opacity-40" />
                                  </span>
                                </a>
                              );

                              if (sidebarCollapsed) {
                                return (
                                  <Tooltip key={child.href}>
                                    <TooltipTrigger asChild>
                                      {externalChildContent}
                                    </TooltipTrigger>
                                    <TooltipContent side="right" className="hidden lg:block">
                                      {child.label}
                                    </TooltipContent>
                                  </Tooltip>
                                );
                              }

                              return externalChildContent;
                            }

                            const isChildActive = location.pathname === child.path || (
                              location.pathname.startsWith(child.path) &&
                              !item.children.some((c) => c.path && c.path !== child.path && c.path.startsWith(child.path) && location.pathname.startsWith(c.path))
                            );
                            
                            const childContent = (
                              <Link
                                key={child.path}
                                to={child.path}
                                className={`sidebar-link text-[12px] py-1.5 ${isChildActive ? 'active' : ''} ${sidebarCollapsed ? 'lg:justify-center lg:px-0' : ''}`}
                                onClick={() => setSidebarOpen(false)}
                                data-testid={`nav-${child.path.split('/').pop()}`}
                              >
                                <child.icon className="h-3.5 w-3.5 flex-shrink-0 opacity-70" />
                                <span className={`truncate ${sidebarCollapsed ? 'lg:hidden' : ''}`}>{child.label}</span>
                              </Link>
                            );

                            if (sidebarCollapsed) {
                              return (
                                <Tooltip key={child.path}>
                                  <TooltipTrigger asChild>
                                    {childContent}
                                  </TooltipTrigger>
                                  <TooltipContent side="right" className="hidden lg:block">
                                    {child.label}
                                  </TooltipContent>
                                </Tooltip>
                              );
                            }

                            return childContent;
                          })}
                        </div>
                      )}
                    </div>
                  );
                }

                // Regular menu item
                const isActive = item.path && (location.pathname === item.path || 
                  (item.path !== '/dashboard' && location.pathname.startsWith(item.path)));
                
                // External link
                if (item.external && item.href) {
                  const externalLinkContent = (
                    <a
                      key={item.href}
                      href={item.href}
                      target="_blank"
                      rel="noopener noreferrer"
                      className={`sidebar-link ${sidebarCollapsed ? 'lg:justify-center lg:px-0' : ''}`}
                      data-testid={`nav-external-pdf-upload`}
                    >
                      <item.icon className="h-[18px] w-[18px] flex-shrink-0" />
                      <span className={`truncate ${sidebarCollapsed ? 'lg:hidden' : ''}`}>
                        {item.label}
                        <ExternalLink className="h-2.5 w-2.5 ml-1 inline-block opacity-40 flex-shrink-0" />
                      </span>
                    </a>
                  );

                  if (sidebarCollapsed) {
                    return (
                      <Tooltip key={item.href}>
                        <TooltipTrigger asChild>
                          {externalLinkContent}
                        </TooltipTrigger>
                        <TooltipContent side="right" className="hidden lg:block">
                          {item.label}
                        </TooltipContent>
                      </Tooltip>
                    );
                  }

                  return externalLinkContent;
                }
                
                const linkContent = (
                  <Link
                    key={item.path}
                    to={item.path}
                    className={`sidebar-link ${isActive ? 'active' : ''} ${sidebarCollapsed ? 'lg:justify-center lg:px-0' : ''}`}
                    onClick={() => setSidebarOpen(false)}
                    data-testid={`nav-${item.path.split('/').pop()}`}
                  >
                    <item.icon className="h-[18px] w-[18px] flex-shrink-0" />
                    <span className={`truncate ${sidebarCollapsed ? 'lg:hidden' : ''}`}>
                      {item.label}
                    </span>
                    {item.badge > 0 && !sidebarCollapsed && (
                      <span className="ml-auto min-w-[18px] h-[18px] flex items-center justify-center bg-red-500 text-white text-[10px] font-bold rounded-full px-1 flex-shrink-0">
                        {item.badge > 99 ? '99+' : item.badge}
                      </span>
                    )}
                    {item.badge > 0 && sidebarCollapsed && (
                      <span className="absolute -top-1 -right-1 min-w-[14px] h-3.5 flex items-center justify-center bg-red-500 text-white text-[9px] font-bold rounded-full px-0.5 lg:flex hidden">
                        {item.badge > 9 ? '9+' : item.badge}
                      </span>
                    )}
                  </Link>
                );

                if (sidebarCollapsed) {
                  return (
                    <Tooltip key={item.path}>
                      <TooltipTrigger asChild>
                        <div className="relative">
                          {linkContent}
                        </div>
                      </TooltipTrigger>
                      <TooltipContent side="right" className="hidden lg:block">
                        {item.label}
                        {item.badge > 0 && ` (${item.badge})`}
                      </TooltipContent>
                    </Tooltip>
                  );
                }

                return linkContent;
              })}
            </TooltipProvider>
          </nav>

          {/* Collapse Toggle Button - Desktop Only */}
          <div className="hidden lg:block px-3 py-2">
            <div className="sidebar-group-divider mb-2" />
            <button
              onClick={() => setSidebarCollapsed(!sidebarCollapsed)}
              className={`w-full flex items-center gap-2.5 px-3 py-2 rounded-lg text-slate-400 dark:text-slate-500 hover:text-slate-600 dark:hover:text-slate-300 hover:bg-slate-50 dark:hover:bg-slate-800/50 transition-all duration-200 ${sidebarCollapsed ? 'justify-center' : ''}`}
              title={sidebarCollapsed ? t('common.expandMenu') : t('common.collapseMenu')}
            >
              {sidebarCollapsed ? (
                <PanelLeftOpen className="h-4 w-4" />
              ) : (
                <>
                  <PanelLeftClose className="h-4 w-4" />
                  <span className="text-xs font-medium">{t('common.collapseMenu') || 'Menüyü Daralt'}</span>
                </>
              )}
            </button>
          </div>

          {/* User Info - Click to Lock */}
          <div className={`px-3 pb-3 ${sidebarCollapsed ? 'lg:px-2 lg:pb-2' : ''}`}>
            <button
              onClick={handleManualLock}
              className={`w-full flex items-center gap-3 p-2.5 rounded-xl bg-slate-50/80 dark:bg-slate-800/50 hover:bg-slate-100 dark:hover:bg-slate-800/80 border border-slate-200/50 dark:border-slate-700/30 transition-all duration-200 group cursor-pointer ${sidebarCollapsed ? 'lg:justify-center lg:p-2' : ''}`}
              title={t('lockScreen.lockScreen')}
              data-testid="sidebar-lock-btn"
            >
              {profilePhotoUrl ? (
                <img 
                  src={profilePhotoUrl} 
                  alt={user?.full_name}
                  className="w-9 h-9 rounded-full object-cover ring-2 ring-white dark:ring-slate-700 shadow-sm flex-shrink-0"
                />
              ) : (
                <div className="w-9 h-9 rounded-full bg-gradient-to-br from-blue-500 to-indigo-600 flex items-center justify-center text-white font-semibold text-sm shadow-sm shadow-blue-500/20 flex-shrink-0">
                  {user?.first_name?.[0] || user?.full_name?.[0] || 'U'}
                </div>
              )}
              {/* Kullanıcı bilgisi — mobile'da her zaman, desktop'ta
                  sidebarCollapsed ise gizle. */}
              <div className={`flex-1 min-w-0 text-left ${sidebarCollapsed ? 'lg:hidden' : ''}`}>
                <p className="text-sm font-semibold text-slate-800 dark:text-slate-100 truncate leading-tight">{user?.full_name}</p>
                <p className="text-[11px] text-slate-400 dark:text-slate-500 truncate leading-tight mt-0.5">{user?.email}</p>
              </div>
              <Lock className={`h-3.5 w-3.5 text-slate-300 dark:text-slate-600 group-hover:text-amber-500 dark:group-hover:text-amber-400 transition-colors flex-shrink-0 ${sidebarCollapsed ? 'lg:hidden' : ''}`} />
            </button>
          </div>
        </div>
      </aside>

      {/* Main Content */}
      <div className={`transition-all duration-300 ${sidebarCollapsed ? 'lg:pl-[72px]' : 'lg:pl-64'}`}>
        {/* Impersonate Banner */}
        {isImpersonating && (
          <div className="sticky top-0 z-50 bg-amber-500 text-white shadow-md" data-testid="impersonate-banner">
            <div className="flex items-center justify-between px-4 py-1.5 gap-2">
              <div className="flex items-center gap-2 text-sm font-medium min-w-0">
                <ShieldAlert className="h-4 w-4 shrink-0" />
                <span className="truncate">
                  <span className="font-bold">{user?.full_name || user?.email}</span>
                  {' '}{t('admin.impersonateAs') || 'olarak görüntülüyorsunuz'}
                </span>
              </div>
              <Button
                size="sm"
                variant="secondary"
                onClick={handleReturnToAdmin}
                className="gap-1.5 bg-white text-amber-700 hover:bg-amber-50 font-semibold h-7 text-xs shrink-0 shadow-sm"
                data-testid="return-to-admin-btn"
              >
                <ShieldAlert className="h-3.5 w-3.5" />
                <span className="hidden sm:inline">{originalAdminName}</span>
                {' '}{t('admin.returnToAdmin') || 'hesabına geri dön'}
              </Button>
            </div>
          </div>
        )}

        {/* Top Header */}
        <header className="sticky top-0 z-30 bg-white/80 dark:bg-slate-900/80 backdrop-blur-xl border-b border-slate-200/50 dark:border-slate-700/30">
          <div className="flex items-center justify-between h-16 px-4 lg:px-8">
            <button 
              className="lg:hidden p-2 hover:bg-slate-100 dark:hover:bg-slate-800 rounded-lg"
              onClick={() => setSidebarOpen(true)}
              data-testid="mobile-menu-btn"
            >
              <Menu className="h-5 w-5 text-slate-600 dark:text-slate-300" />
            </button>

            <div className="flex-1 min-w-0 flex items-center">
              {FAV_ROLES.includes(user?.role) && <FavoriteStar pathname={location.pathname} />}
            </div>
            {(user?.role === 'SUPER_ADMIN' || user?.role === 'FULL_ADMIN' || user?.role === 'FIRM_ADMIN') && (
              <div className="hidden lg:block relative" ref={searchRef} data-testid="header-search">
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                  <input
                    type="text"
                    value={searchQuery}
                    onChange={(e) => handleSearch(e.target.value)}
                    placeholder={t('common.globalSearch') || 'Kullanıcı veya plaka ara...'}
                    className="w-64 pl-9 pr-3 py-1.5 text-sm bg-slate-100 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500/50 focus:border-blue-500 transition-all placeholder:text-slate-400"
                    data-testid="header-search-input"
                  />
                </div>
                {searchOpen && searchResults.length > 0 && (
                  <div className="absolute top-full mt-1 left-0 w-80 bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-lg shadow-xl z-50 overflow-hidden" data-testid="search-results-dropdown">
                    {searchResults.map((item) => item._type === 'vehicle' ? (
                      <button
                        key={`v-${item.id}`}
                        onClick={() => handleSearchSelect(item)}
                        className="w-full text-left px-3 py-2.5 hover:bg-slate-50 dark:hover:bg-slate-700 flex items-center gap-3 transition-colors border-b border-slate-100 dark:border-slate-700 last:border-0"
                        data-testid={`search-result-vehicle-${item.id}`}
                      >
                        <div className="h-8 w-8 rounded-full bg-amber-100 dark:bg-amber-900/30 flex items-center justify-center flex-shrink-0">
                          <Truck className="h-4 w-4 text-amber-600 dark:text-amber-400" />
                        </div>
                        <div className="min-w-0">
                          <p className="text-sm font-medium text-slate-800 dark:text-slate-200 truncate">{item.plate_number || item.license_plate}</p>
                          <p className="text-xs text-slate-400 truncate">{item.brand} {item.model}</p>
                          {item.current_driver_name ? (
                            <p className="text-[10px] text-emerald-600 dark:text-emerald-400 truncate flex items-center gap-0.5 mt-0.5" data-testid={`search-driver-${item.id}`}>
                              <User className="h-2.5 w-2.5" />
                              {item.current_driver_name}
                            </p>
                          ) : (
                            <p className="text-[10px] text-slate-400 truncate flex items-center gap-0.5 mt-0.5">
                              <User className="h-2.5 w-2.5" />
                              {t('common.unassigned') || 'Atanmamış'}
                            </p>
                          )}
                        </div>
                        <span className="ml-auto text-[10px] px-1.5 py-0.5 rounded-full bg-amber-100 text-amber-700 flex-shrink-0">{t('common.vehicleBadge')}</span>
                      </button>
                    ) : (
                      <button
                        key={`u-${item.id}`}
                        onClick={() => handleSearchSelect(item)}
                        className="w-full text-left px-3 py-2.5 hover:bg-slate-50 dark:hover:bg-slate-700 flex items-center gap-3 transition-colors border-b border-slate-100 dark:border-slate-700 last:border-0"
                        data-testid={`search-result-${item.id}`}
                      >
                        <div className="h-8 w-8 rounded-full bg-blue-100 dark:bg-blue-900/30 flex items-center justify-center flex-shrink-0">
                          <User className="h-4 w-4 text-blue-600 dark:text-blue-400" />
                        </div>
                        <div className="min-w-0">
                          <p className="text-sm font-medium text-slate-800 dark:text-slate-200 truncate">{item.full_name}</p>
                          <p className="text-xs text-slate-400 truncate">{item.email}</p>
                        </div>
                        <span className={`ml-auto text-[10px] px-1.5 py-0.5 rounded-full flex-shrink-0 ${item.status === 'ACTIVE' ? 'bg-emerald-100 text-emerald-700' : item.status === 'SUSPENDED' ? 'bg-red-100 text-red-700' : 'bg-amber-100 text-amber-700'}`}>
                          {item.status}
                        </span>
                      </button>
                    ))}
                  </div>
                )}
                {searchOpen && searchQuery.length >= 2 && searchResults.length === 0 && (
                  <div className="absolute top-full mt-1 left-0 w-80 bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-lg shadow-xl z-50 p-4 text-center text-sm text-slate-400">
                    {t('common.noResults') || 'Sonuç bulunamadı'}
                  </div>
                )}
              </div>
            )}

            {/* Mobile Search Button */}
            {(user?.role === 'SUPER_ADMIN' || user?.role === 'FULL_ADMIN' || user?.role === 'FIRM_ADMIN') && (
              <button
                className="lg:hidden p-2 hover:bg-slate-100 dark:hover:bg-slate-800 rounded-lg"
                onClick={() => { setMobileSearchOpen(true); setTimeout(() => document.getElementById('mobile-search-input')?.focus(), 100); }}
                data-testid="mobile-search-btn"
              >
                <Search className="h-5 w-5 text-slate-600 dark:text-slate-300" />
              </button>
            )}

            <div className="flex items-center gap-1 md:gap-3">
              {/* Bekleyen Planlama Formu Rozeti (Faz 2.5) */}
              {pendingForms > 0 && (
                <Link to="/dashboard" className="relative" data-testid="pending-forms-nav">
                  <Button variant="ghost" size="sm" className="gap-2 relative">
                    <ClipboardList className="h-4 w-4 text-rose-500" />
                    <span className="absolute -top-1 -right-1 min-w-[18px] h-[18px] flex items-center justify-center bg-rose-500 text-white text-[10px] font-bold rounded-full px-1 animate-pulse">
                      {pendingForms}
                    </span>
                  </Button>
                </Link>
              )}

              {/* Notification Bell */}
              <Link to="/dashboard/my-notifications" className="relative">
                <Button 
                  variant="ghost" 
                  size="sm" 
                  className="gap-2 relative"
                  data-testid="notification-bell-btn"
                >
                  <Bell className="h-4 w-4" />
                  {unreadNotifications > 0 && (
                    <span className="absolute -top-1 -right-1 min-w-[18px] h-[18px] flex items-center justify-center bg-red-500 text-white text-[10px] font-bold rounded-full px-1 animate-pulse">
                      {unreadNotifications > 99 ? '99+' : unreadNotifications}
                    </span>
                  )}
                </Button>
              </Link>

              {/* Lock Screen Button */}
              <TooltipProvider>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Button 
                      variant="ghost" 
                      size="sm" 
                      onClick={handleManualLock}
                      className="gap-2 hover:bg-amber-50 dark:hover:bg-amber-900/20 hover:text-amber-600 dark:hover:text-amber-400 transition-colors"
                      data-testid="header-lock-btn"
                    >
                      <Lock className="h-4 w-4" />
                    </Button>
                  </TooltipTrigger>
                  <TooltipContent>{t('lockScreen.lockScreen')}</TooltipContent>
                </Tooltip>
              </TooltipProvider>

              {/* Theme Toggle Button */}
              <Button 
                variant="ghost" 
                size="sm" 
                onClick={toggleTheme}
                className="gap-2"
                data-testid="theme-toggle-btn"
              >
                {theme === 'light' ? (
                  <Moon className="h-4 w-4" />
                ) : (
                  <Sun className="h-4 w-4" />
                )}
              </Button>

              {/* Privacy / Sunum Modu — tüm tutarları blur'lar (Ctrl+Shift+H).
                  Mobilde header'ı şişirmemek için gizli; profil menüsüne taşındı. */}
              <TooltipProvider>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={togglePrivacyMode}
                      className={`hidden md:inline-flex gap-2 ${privacyMode ? 'text-orange-500 hover:text-orange-600 dark:text-orange-400 dark:hover:text-orange-300' : ''}`}
                      data-testid="privacy-mode-toggle"
                      aria-pressed={privacyMode}
                      aria-label={privacyMode ? 'Sunum modunu kapat' : 'Sunum modunu aç'}
                    >
                      {privacyMode ? (
                        <EyeOff className="h-4 w-4" />
                      ) : (
                        <Eye className="h-4 w-4" />
                      )}
                    </Button>
                  </TooltipTrigger>
                  <TooltipContent>
                    {privacyMode ? 'Sunum modu açık — tutarlar gizli (Ctrl+Shift+H)' : 'Sunum modu — tutarları gizle (Ctrl+Shift+H)'}
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>

              {/* Color Theme Selector - hidden on mobile, shown in user menu instead */}
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="ghost" size="sm" className="hidden md:inline-flex gap-2" data-testid="color-theme-selector">
                    <Palette className="h-4 w-4" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="min-w-[160px]">
                  {COLOR_THEMES.map((ct) => (
                    <DropdownMenuItem 
                      key={ct.id} 
                      onClick={() => setColorTheme(ct.id)}
                      className="flex items-center gap-3 cursor-pointer"
                      data-testid={`color-theme-${ct.id}`}
                    >
                      <div 
                        className="w-4 h-4 rounded-full flex-shrink-0 ring-1 ring-black/10" 
                        style={{ backgroundColor: ct.color }} 
                      />
                      <span className="text-sm">{ct.label}</span>
                      {colorTheme === ct.id && (
                        <Check className="h-3.5 w-3.5 ml-auto opacity-70" />
                      )}
                    </DropdownMenuItem>
                  ))}
                </DropdownMenuContent>
              </DropdownMenu>

              {/* Language Selector */}
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="ghost" size="sm" className="gap-1 md:gap-2" data-testid="language-selector">
                    <Globe className="h-4 w-4" />
                    <span className="hidden sm:inline uppercase text-xs font-medium">{language}</span>
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end">
                  <DropdownMenuItem onClick={() => handleLanguageChange('tr')} data-testid="lang-tr">
                    🇹🇷 Türkçe
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => handleLanguageChange('en')} data-testid="lang-en">
                    🇬🇧 English
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => handleLanguageChange('nl')} data-testid="lang-nl">
                    🇳🇱 Nederlands
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => handleLanguageChange('fr')} data-testid="lang-fr">
                    🇫🇷 Français
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>

              {/* User Menu */}
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="ghost" className="gap-2 h-auto py-1.5" data-testid="user-menu-btn">
                    {profilePhotoUrl ? (
                      <img 
                        src={profilePhotoUrl} 
                        alt="Profile" 
                        className="w-9 h-9 rounded-full object-cover border-2 border-white dark:border-slate-700 shadow-sm"
                      />
                    ) : (
                      <div className="w-9 h-9 rounded-full bg-gradient-to-br from-blue-500 to-indigo-600 flex items-center justify-center text-white font-semibold text-sm">
                        {user?.full_name?.charAt(0)?.toUpperCase() || 'U'}
                      </div>
                    )}
                    <div className="hidden sm:block text-left">
                      <p className="text-sm font-medium text-slate-800 dark:text-slate-100 leading-tight">{user?.full_name}</p>
                      <p className="text-xs text-slate-400 leading-tight">
                        {firmInfo?.name || user?.role?.replace('_', ' ')}
                      </p>
                    </div>
                    <ChevronDown className="h-4 w-4 text-slate-400" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-64">
                  <div className="px-3 py-3 bg-slate-50 dark:bg-slate-800 rounded-t-lg">
                    <div className="flex items-center gap-3">
                      {profilePhotoUrl ? (
                        <img 
                          src={profilePhotoUrl} 
                          alt="Profile" 
                          className="w-10 h-10 rounded-full object-cover border-2 border-white dark:border-slate-700 shadow-sm"
                        />
                      ) : (
                        <div className="w-10 h-10 rounded-full bg-gradient-to-br from-blue-500 to-indigo-600 flex items-center justify-center text-white font-semibold">
                          {user?.full_name?.charAt(0)?.toUpperCase() || 'U'}
                        </div>
                      )}
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-semibold text-slate-900 dark:text-slate-100 truncate">{user?.full_name}</p>
                        <p className="text-xs text-slate-500 dark:text-slate-400 truncate">{user?.email}</p>
                      </div>
                    </div>
                    {firmInfo && (
                      <div className="mt-2 pt-2 border-t border-slate-200 dark:border-slate-700">
                        <div className="flex items-center gap-2">
                          <Building2 className="h-3.5 w-3.5 text-slate-400" />
                          <span className="text-xs text-slate-600 dark:text-slate-300">{firmInfo.name}</span>
                        </div>
                      </div>
                    )}
                  </div>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem asChild>
                    <Link to="/dashboard/profile" className="cursor-pointer">
                      <User className="mr-2 h-4 w-4" />
                      {t('common.profile')}
                    </Link>
                  </DropdownMenuItem>
                  {/* Mobile-only: Privacy / Sunum Modu (Renk teması üzerinde) */}
                  <DropdownMenuSeparator className="md:hidden" />
                  <DropdownMenuItem
                    onClick={togglePrivacyMode}
                    className="md:hidden cursor-pointer"
                    data-testid="privacy-mode-toggle-mobile"
                  >
                    {privacyMode ? (
                      <EyeOff className="mr-2 h-4 w-4 text-orange-500" />
                    ) : (
                      <Eye className="mr-2 h-4 w-4" />
                    )}
                    <span className={privacyMode ? 'text-orange-500 font-medium' : ''}>
                      {privacyMode ? 'Sunum Modu (Açık)' : 'Sunum Modu'}
                    </span>
                  </DropdownMenuItem>
                  {/* Mobile-only: Color Theme under profile */}
                  <DropdownMenuSeparator className="md:hidden" />
                  <div className="md:hidden px-2 py-1.5">
                    <p className="text-xs text-slate-500 px-2 mb-1.5">{t('common.colorTheme') || 'Renk Teması'}</p>
                    <div className="flex items-center gap-1.5 px-2">
                      {COLOR_THEMES.map((ct) => (
                        <button
                          key={ct.id}
                          onClick={() => setColorTheme(ct.id)}
                          className={`w-6 h-6 rounded-full ring-1 ring-black/10 transition-all ${
                            colorTheme === ct.id ? 'ring-2 ring-offset-2 ring-primary scale-110' : 'hover:scale-105'
                          }`}
                          style={{ backgroundColor: ct.color }}
                          title={ct.label}
                          data-testid={`mobile-color-theme-${ct.id}`}
                        />
                      ))}
                    </div>
                  </div>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem onClick={handleLogout} className="text-red-600 cursor-pointer" data-testid="logout-btn">
                    <LogOut className="mr-2 h-4 w-4" />
                    {t('common.logout')}
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          </div>
        </header>

        {/* Mobile Search Overlay */}
        {mobileSearchOpen && (user?.role === 'SUPER_ADMIN' || user?.role === 'FULL_ADMIN' || user?.role === 'FIRM_ADMIN') && (
          <div className="lg:hidden fixed inset-0 z-50 bg-black/40" onClick={() => { setMobileSearchOpen(false); setSearchOpen(false); setSearchQuery(''); }}>
            <div className="bg-white dark:bg-slate-900 p-3 shadow-lg" onClick={(e) => e.stopPropagation()} ref={searchRef}>
              <div className="flex items-center gap-2">
                <div className="relative flex-1">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                  <input
                    id="mobile-search-input"
                    type="text"
                    value={searchQuery}
                    onChange={(e) => handleSearch(e.target.value)}
                    placeholder={t('common.globalSearch') || 'Kullanıcı veya plaka ara...'}
                    className="w-full pl-9 pr-3 py-2.5 text-sm bg-slate-100 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500/50 focus:border-blue-500 placeholder:text-slate-400"
                    data-testid="mobile-search-input"
                  />
                </div>
                <button
                  onClick={() => { setMobileSearchOpen(false); setSearchOpen(false); setSearchQuery(''); }}
                  className="p-2 hover:bg-slate-100 dark:hover:bg-slate-800 rounded-lg flex-shrink-0"
                  data-testid="mobile-search-close"
                >
                  <X className="h-5 w-5 text-slate-500" />
                </button>
              </div>
              {/* Mobile Search Results */}
              {searchOpen && searchResults.length > 0 && (
                <div className="mt-2 max-h-[60vh] overflow-y-auto rounded-lg border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800">
                  {searchResults.map((item) => item._type === 'vehicle' ? (
                    <button
                      key={`mv-${item.id}`}
                      onClick={() => { handleSearchSelect(item); setMobileSearchOpen(false); }}
                      className="w-full text-left px-3 py-3 hover:bg-slate-50 dark:hover:bg-slate-700 flex items-center gap-3 border-b border-slate-100 dark:border-slate-700 last:border-0"
                    >
                      <div className="h-9 w-9 rounded-full bg-amber-100 dark:bg-amber-900/30 flex items-center justify-center flex-shrink-0">
                        <Truck className="h-4 w-4 text-amber-600 dark:text-amber-400" />
                      </div>
                      <div className="min-w-0 flex-1">
                        <p className="text-sm font-medium text-slate-800 dark:text-slate-200 truncate">{item.plate_number || item.license_plate}</p>
                        <p className="text-xs text-slate-400 truncate">{item.brand} {item.model}</p>
                      </div>
                      <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-amber-100 text-amber-700 flex-shrink-0">{t('common.vehicleBadge')}</span>
                    </button>
                  ) : (
                    <button
                      key={`mu-${item.id}`}
                      onClick={() => { handleSearchSelect(item); setMobileSearchOpen(false); }}
                      className="w-full text-left px-3 py-3 hover:bg-slate-50 dark:hover:bg-slate-700 flex items-center gap-3 border-b border-slate-100 dark:border-slate-700 last:border-0"
                    >
                      <div className="h-9 w-9 rounded-full bg-blue-100 dark:bg-blue-900/30 flex items-center justify-center flex-shrink-0">
                        <User className="h-4 w-4 text-blue-600 dark:text-blue-400" />
                      </div>
                      <div className="min-w-0 flex-1">
                        <p className="text-sm font-medium text-slate-800 dark:text-slate-200 truncate">{item.full_name}</p>
                        <p className="text-xs text-slate-400 truncate">{item.email}</p>
                      </div>
                      <span className={`text-[10px] px-1.5 py-0.5 rounded-full flex-shrink-0 ${item.status === 'ACTIVE' ? 'bg-emerald-100 text-emerald-700' : item.status === 'SUSPENDED' ? 'bg-red-100 text-red-700' : 'bg-amber-100 text-amber-700'}`}>
                        {item.status}
                      </span>
                    </button>
                  ))}
                </div>
              )}
              {searchOpen && searchQuery.length >= 2 && searchResults.length === 0 && (
                <div className="mt-2 p-4 text-center text-sm text-slate-400 bg-white dark:bg-slate-800 rounded-lg border border-slate-200 dark:border-slate-700">
                  {t('common.noResults') || 'Sonuç bulunamadı'}
                </div>
              )}
            </div>
          </div>
        )}

        {/* Page Content */}
        <main className="p-4 lg:p-8">
          <Outlet />
        </main>
        {FAV_ROLES.includes(user?.role) && <FavoritesWheel />}
      </div>

      {/* Install banner / update toast / offline bar — single mount for the app */}
      <PWAStatus />

      {/* New Message Popup Notification */}
      {msgPopup && (
        <div className="fixed top-4 right-4 z-[60] w-80 max-w-[calc(100vw-2rem)] animate-in slide-in-from-top-2 fade-in duration-300" data-testid="msg-popup">
          <div className="bg-white dark:bg-slate-800 rounded-xl shadow-2xl border border-slate-200 dark:border-slate-700 overflow-hidden">
            <div className="bg-gradient-to-r from-blue-500 to-indigo-500 px-4 py-2 flex items-center justify-between">
              <div className="flex items-center gap-2 text-white">
                <Mail className="h-4 w-4" />
                <span className="text-xs font-semibold">Yeni Mesaj</span>
              </div>
              <button onClick={() => setMsgPopup(null)} className="text-white/70 hover:text-white transition-colors">
                <X className="h-3.5 w-3.5" />
              </button>
            </div>
            <div className="px-4 py-3">
              <div className="flex items-center gap-2 mb-1.5">
                <div className="w-8 h-8 rounded-full bg-blue-100 dark:bg-blue-900/40 flex items-center justify-center shrink-0">
                  <User className="h-4 w-4 text-blue-500" />
                </div>
                <div className="min-w-0 flex-1">
                  <p className="text-sm font-semibold text-slate-800 dark:text-slate-100 truncate">{msgPopup.sender_name}</p>
                  <p className="text-xs text-slate-500 truncate">{msgPopup.subject}</p>
                </div>
              </div>
              {msgPopup.body_preview && (
                <p className="text-xs text-slate-400 line-clamp-2 mt-1">
                  {msgPopup.body_preview.replace(/<[^>]*>/g, '').replace(/&nbsp;/g, ' ').replace(/&amp;/g, '&').replace(/&lt;/g, '<').replace(/&gt;/g, '>').replace(/&quot;/g, '"').trim()}
                </p>
              )}
              <button
                onClick={() => { setMsgPopup(null); navigate('/dashboard/messages'); }}
                className="mt-2 w-full text-center py-1.5 text-xs font-medium text-blue-600 hover:bg-blue-50 dark:hover:bg-blue-900/20 rounded-lg transition-colors"
              >
                Mesajı Görüntüle
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Old auto-dismissing chat popup replaced by MessengerChatHeads */}

      {/* Profile Completion Required Modal */}
      <Dialog open={showProfileModal} onOpenChange={setShowProfileModal}>
        <DialogContent className="max-w-md" onInteractOutside={(e) => e.preventDefault()}>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <User className="h-5 w-5 text-blue-600" />
              {t('employeeProfile.completeProfileTitle')}
            </DialogTitle>
            <DialogDescription>
              {t('employeeProfile.completeProfileDesc')}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button onClick={() => { setShowProfileModal(false); navigate('/dashboard/my-profile'); }}>
              {t('employeeProfile.goToProfile')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Sticky Notes Widget - visible on all pages */}
      <StickyNotesWidget />
      <MessengerChatHeads />
    </div>
  );
};

export default DashboardLayout;
